/**
 * Copyright © 2026 Delicious AI, LLC
 */

import type {
  SabreGetProductQuery,
  SabreGetProductQueryVariables,
  SabreGetProductsSearchQuery,
} from '@dai/graphql'

import type { ClassificationBoundingBox } from './classificationTypes'

export type ProductByUpcData = SabreGetProductQuery

export type ProductByUpcVariables = SabreGetProductQueryVariables

export type ProductDetails = NonNullable<ProductByUpcData['product']>
export type SearchProduct =
  SabreGetProductsSearchQuery['products']['results'][number]

export type PreviewContextBox = Pick<
  ClassificationBoundingBox,
  | 'id'
  | 'minX'
  | 'maxX'
  | 'minY'
  | 'maxY'
  | 'metaClass'
  | 'isFlagged'
  | 'isHumanDeleted'
> & {
  currentUpc: string | null
  verifiedUpc: string | null
}

export type ClassificationPreviewContextData = {
  classificationPreviewContext: {
    sourceImageUrl: string
    nearbyBoundingBoxes: PreviewContextBox[]
  } | null
}

export type ClassificationPreviewContextVariables = {
  boxId: string
}

export const formatPackagingLabel = (
  packaging: ProductDetails['packaging'],
) => {
  if (!packaging) return null

  const parts = [
    packaging.quantity ? String(packaging.quantity) : null,
    packaging.container
      ? packaging.container
          .toLowerCase()
          .split('_')
          .filter((part: string) => part.length > 0)
          .map((part: string) => part[0].toUpperCase() + part.slice(1))
          .join(' ')
      : null,
    packaging.size && packaging.unit
      ? `${packaging.size}${packaging.unit}`
      : null,
  ].filter(Boolean)

  return parts.length ? parts.join(' ') : null
}

export const getProductDisplayName = (product: ProductDetails | null) => {
  if (!product) return null

  const primary = product.commonName?.name || product.name || product.upcA
  const packaging =
    product.packaging?.packagingString ||
    formatPackagingLabel(product.packaging)
  const brand = product.brand?.name

  return [primary, packaging, brand].filter(Boolean).join(' · ')
}

export const getSearchProductDisplayName = (product: SearchProduct | null) => {
  if (!product) return null

  const primary = product.preferredName || product.name || product.upcA
  const packaging = product.packaging?.packagingString || null
  const brand = product.brand?.name

  return [primary, packaging, brand].filter(Boolean).join(' · ')
}

export const getMatchingProductByUpc = (
  product: ProductDetails | null,
  upc: string | null | undefined,
) => {
  if (!product || !upc) return null

  return product.upcA === upc || product.upcE === upc ? product : null
}
